import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { useCart } from '../context/CartContext';
import { ShoppingCart, Search, Filter, X, Loader2, Package } from 'lucide-react';
import { ShopGridSkeleton } from '../components/PageSkeletons';
import { toast } from 'sonner';
import AdBanner from '../components/AdBanner';
import { getProductImageUrl } from '../utils/imageUtils';
import { formatSellingUnit, formatUnitConversion } from './admin-products/catalogUnits';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Shop = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const { addToCart } = useCart();

  // Fetch categories on mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await axios.get(`${API}/categories`);
        setCategories(res.data.categories || []);
      } catch (error) {
        console.error('Shop: failed to fetch categories', error);
      }
    };
    fetchCategories();
  }, []);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => setSearchTerm(searchInput), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Fetch products when filters change
  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (selectedCategory !== 'all') params.category = selectedCategory;
      if (searchTerm) params.search = searchTerm;

      const response = await axios.get(`${API}/products`, { params });
      setProducts(response.data.products || []);
    } catch (error) {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, searchTerm]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const handleAddToCart = (product) => {
    const minOrder = product.min_order_quantity || 1;
    addToCart({
      id: product._id,
      name: product.name,
      price: product.price,
      unit: product.unit,
      unit_value: product.unit_value || 1,
      image: getProductImageUrl(product),
      min_order_quantity: minOrder
    }, minOrder);
    toast.success(`${product.name} added to cart!`);
  };

  const clearFilters = () => {
    setSelectedCategory('all');
    setSearchInput('');
    setSearchTerm('');
  };

  const hasFilters = selectedCategory !== 'all' || searchTerm;
  const isAvailable = (product) => product.availability
    ? product.availability === 'in_stock'
    : Number(product.stock) > 0;

  return (
    <div data-testid="shop-page">
      {/* Hero */}
      <section className="py-16 px-4 bg-gradient-to-br from-green-50 via-white to-green-50">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">Farm Shop</h1>
          <p className="text-lg text-gray-600 mb-8 max-w-xl mx-auto">
            Fresh produce, livestock, and processed goods — straight from Daye Farm to your table.
          </p>

          {/* Search */}
          <div className="max-w-xl mx-auto relative" data-testid="shop-search">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 h-5 w-5" />
            <Input
              type="text"
              placeholder="Search products..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pl-12 h-12 text-base"
              data-testid="shop-search-input"
            />
            {searchInput && (
              <button onClick={() => setSearchInput('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Category Filters */}
      <section className="py-4 px-4 bg-white border-b sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide" data-testid="category-filters">
            <Button
              onClick={() => setSelectedCategory('all')}
              variant={selectedCategory === 'all' ? 'default' : 'outline'}
              size="sm"
              className={selectedCategory === 'all' ? 'bg-green-600 hover:bg-green-700' : ''}
              data-testid="category-all"
            >
              All Products
            </Button>
            {categories.map((cat) => (
              <Button
                key={cat.slug || cat._id}
                onClick={() => setSelectedCategory(cat.slug)}
                variant={selectedCategory === cat.slug ? 'default' : 'outline'}
                size="sm"
                className={selectedCategory === cat.slug ? 'bg-green-600 hover:bg-green-700 whitespace-nowrap' : 'whitespace-nowrap'}
                data-testid={`category-${cat.slug}`}
              >
                {cat.name}
              </Button>
            ))}
            {hasFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="text-gray-500 ml-2 whitespace-nowrap" data-testid="clear-filters">
                <X className="h-3.5 w-3.5 mr-1" /> Clear
              </Button>
            )}
          </div>
        </div>
      </section>

      {/* Ad Banner */}
      <section className="py-6 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <AdBanner placement="shop" />
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-12 px-4 bg-gray-50 min-h-[400px]">
        <div className="max-w-7xl mx-auto">
          {/* Result count */}
          {!loading && (
            <p className="text-sm text-gray-500 mb-4" data-testid="product-count">
              {products.length} product{products.length !== 1 ? 's' : ''} found
              {selectedCategory !== 'all' && ` in "${categories.find(c => c.slug === selectedCategory)?.name || selectedCategory}"`}
              {searchTerm && ` matching "${searchTerm}"`}
            </p>
          )}

          {loading ? (
            <ShopGridSkeleton count={8} />
          ) : products.length === 0 ? (
            <div className="text-center py-16">
              <Package className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-lg text-gray-500 mb-2">No products found</p>
              <p className="text-sm text-gray-400 mb-4">Try adjusting your filters or search term</p>
              {hasFilters && (
                <Button variant="outline" onClick={clearFilters} data-testid="clear-filters-empty">
                  Clear Filters
                </Button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {products.map((product) => (
                <Card key={product._id} className="group hover:shadow-xl transition-all duration-300 overflow-hidden" data-testid={`product-card-${product._id}`}>
                  <Link to={`/product/${product._id}`}>
                    <div className="h-52 overflow-hidden bg-gray-100">
                      <img
                        src={getProductImageUrl(product)}
                        alt={product.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        onError={(e) => { e.target.src = 'https://via.placeholder.com/400x300?text=No+Image'; }}
                        loading="lazy"
                      />
                    </div>
                  </Link>
                  <CardContent className="p-4">
                    <Link to={`/product/${product._id}`}>
                      <h3 className="font-semibold text-gray-900 mb-1 group-hover:text-green-600 transition-colors line-clamp-1">
                        {product.name}
                      </h3>
                    </Link>
                    {product.category && (
                      <span className="text-[11px] px-2 py-0.5 bg-green-50 text-green-700 rounded-full inline-block mb-2">
                        {categories.find(c => c.slug === product.category)?.name || product.category}
                      </span>
                    )}
                    <p className="text-sm text-gray-500 line-clamp-2 mb-3">{product.description}</p>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xl font-bold text-green-600">
                        ₦{(product.price || 0).toLocaleString()}
                      </span>
                      <span className="text-xs text-gray-500" data-testid={`shop-product-unit-${product._id}`}>
                        per {formatSellingUnit(product.unit_value, product.unit)}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mb-2" data-testid={`shop-product-conversion-${product._id}`}>
                      {formatUnitConversion(product.unit_value, product.unit)}
                    </p>
                    {product.min_order_quantity > 1 && (
                      <p className="text-xs text-orange-600 bg-orange-50 px-2 py-1 rounded mb-2">
                        MOQ: {product.min_order_quantity} sale units
                      </p>
                    )}
                    <div className="flex items-center justify-between pt-2 border-t">
                      <span className={`text-xs ${isAvailable(product) ? 'text-green-600' : 'text-red-500'}`} data-testid={`shop-product-availability-${product._id}`}>
                        {isAvailable(product) ? 'Available' : 'Out of stock'}
                      </span>
                      <Button
                        onClick={(e) => { e.preventDefault(); handleAddToCart(product); }}
                        disabled={!isAvailable(product)}
                        size="sm"
                        className="h-8 px-3 bg-green-600 hover:bg-green-700"
                        data-testid={`add-to-cart-${product._id}`}
                      >
                        <ShoppingCart className="h-3.5 w-3.5 mr-1" /> Add
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Shop;
